import { gsap } from 'gsap';
import type { ThreeStage } from '../../../../three/ThreeStage';
import { BaseScene } from '../../../../core/BaseScene';
import { draw } from '../../../../components/svg';
import { words, revealWords, settleWords } from '../../../../components/text';
import { ROOT, renderMintTree } from '../../../../components/mintTree';

const ROOT_ORIGIN = `${ROOT.x} ${ROOT.y}`;

/**
 * 0 MINT alone — ONE FOUNDATION.
 * 1 SPECIALISED INTELLIGENCE. — each enabled specialisation grows out of MINT
 * 2 One more branch, deliberately unnamed: MINT is not limited to what exists today
 */
export class MintSpecialisationScene extends BaseScene {
  public readonly stepCount = 3;

  protected template(): string {
    return `
      <div class="scene-layout mint-specialisation">
        <svg class="diagram mt-diagram" viewBox="0 0 1600 900" role="img" aria-label="MINT and the specialisations that grow out of it">
          ${renderMintTree(this.context, { uid: this.meta.id, ghosts: 'one', branchFrom: 1, ghostFrom: 2 })}
        </svg>
        <div class="mt-statements">
          <h2 class="statement" data-from="0">${words('ONE FOUNDATION.')}</h2>
          <h2 class="statement statement--accent" data-from="1">${words('SPECIALISED INTELLIGENCE.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$('.mt-root'), { opacity: 0, scale: 0.6, svgOrigin: ROOT_ORIGIN }, { opacity: 1, scale: 1, duration: 1.3, ease: 'expo.out' }, 0.1);
    tl.add(revealWords(this.$('.mt-statements .statement')), 0.8);
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    gsap.set(this.$('.mt-root'), { opacity: 1, scale: 1, svgOrigin: ROOT_ORIGIN });
    this.$$('.mt-statements .statement').forEach((s) => settleWords(s));

    // Branches grow one after another, each carrying its name and what it is for.
    const branches = this.$$('.mt-branch');
    branches.forEach((branch, i) => {
      const path = branch.querySelector('.mt-branch__path')!;
      const node = branch.querySelectorAll('.mt-branch__node circle');
      const text = branch.querySelector('.mt-branch__text')!;
      if (forward && step === 1) {
        const at = 0.4 + i * 0.55;
        draw(path, false, { immediate: true });
        tl.add(draw(path, true, { duration: 0.8, ease: 'power2.out' }), at);
        tl.fromTo(node, { scale: 0, transformOrigin: '50% 50%' }, { scale: 1, duration: 0.6, ease: 'back.out(2.2)' }, at + 0.6);
        tl.fromTo(text, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, at + 0.75);
      } else {
        draw(path, step >= 1, { immediate: true });
        gsap.set(node, { scale: 1, transformOrigin: '50% 50%' });
        gsap.set(text, { opacity: 1, y: 0 });
      }
    });
    if (forward && step === 1) tl.add(revealWords(this.$$('.mt-statements .statement')[1]), 0.1);

    const ghostPaths = this.$$('.mt-ghost__path');
    const ghostDots = this.$$('.mt-ghost__glow, .mt-ghost__ring');
    if (forward && step === 2) {
      draw(ghostPaths, false, { immediate: true });
      tl.add(draw(ghostPaths, true, { duration: 1.2, ease: 'power2.inOut' }), 0.2);
      tl.fromTo(ghostDots, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 1.2, ease: 'power2.out' }, 1.1);
    } else {
      draw(ghostPaths, step >= 2, { immediate: true });
      gsap.set(ghostDots, { opacity: 1, scale: 1, transformOrigin: '50% 50%' });
    }
  }
}
